/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Button, IconClose } from '../../components';

import css from './Modal.module.css';

const KEY_ESCAPE = 'Escape';
const PAGE_ROOT_ID = 'root';

// How many portal Modals currently hold the page inert.
//
// `inert` on #page-root is one flag for the whole document, but modals can now legitimately stack:
// the booking CTA opens the login dialog on top of the still-open mobile order panel, precisely so
// that dismissing it returns the visitor to the dates they picked. With an unconditional
// removeAttribute, closing the top one handed the page behind the bottom one back to the tab order
// and to screen readers while it was still covered by a full-screen modal. Counting keeps the page
// inert until the last portal modal is gone. Module state on purpose: it describes one
// document-level attribute, which is itself global.
let inertRefCount = 0;

const acquireInert = () => {
  inertRefCount += 1;
  document.getElementById(PAGE_ROOT_ID)?.setAttribute('inert', '');
};

const releaseInert = () => {
  inertRefCount = Math.max(0, inertRefCount - 1);
  if (inertRefCount === 0) {
    document.getElementById(PAGE_ROOT_ID)?.removeAttribute('inert');
  }
};
const PORTAL_ROOT_ID = 'portal-root';

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

/**
 * Modal
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.scrollLayerClassName overwrite components own css.scrollLayer
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.contentClassName overwrite components own css.content
 * @param {string?} props.isClosedClassName overwrite components own css.isClosed
 * @param {ReactNode} props.closeButtonMessage
 * @param {Object} props.lightCloseButton use lighter close button styles
 * @param {string} props.id
 * @param {Object} props.intl
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {boolean} props.usePortal
 * @returns {JSX.Element} Modal element
 */
export class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.refDiv = React.createRef();
    this.vh = null;

    this.holdsInert = false;

    this.state = {
      portalRoot: null,
    };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling, usePortal } = this.props;
    onManageDisableScrolling(id, isOpen);
    if (usePortal && isOpen) {
      this.holdsInert = true;
      acquireInert();
    }
    window.document.body.addEventListener('keyup', this.handleBodyKeyUp);

    // A hack to update container height for mobile Safari,
    // when resizing happens due to scroll.
    // css.isOpenInPortal has "height: calc(var(--vh, 1vh) * 100)"
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
    window.addEventListener('resize', this.handleResize);

    this.setState({
      portalRoot: document.getElementById(PORTAL_ROOT_ID),
    });
  }

  componentDidUpdate(prevProps) {
    const { id, isOpen, onManageDisableScrolling, focusElementId } = prevProps;
    if (this.props.isOpen !== isOpen) {
      onManageDisableScrolling(id, this.props.isOpen);

      // Because we are using portal,
      // we need to set the focus inside Modal manually
      //
      // preventScroll: the portal lives at the end of <body>, so focusing it scrolled the document
      // all the way down to it. That happens in this commit, i.e. before the disableScrolling
      // update reaches Page, so Page captured the bottom of the page as the position to return the
      // visitor to when the modal closes. Same on the way out for the element that opened the
      // modal: Page puts the visitor back exactly where they were, scrolling to the opener fights
      // that.
      if (this.props.usePortal) {
        if (this.props.isOpen) {
          this.refDiv.current.focus({ preventScroll: true });
        } else if (focusElementId) {
          document.getElementById(focusElementId)?.focus({ preventScroll: true });
        }
      }
    }

    // Reconciled unconditionally, outside the isOpen-changed branch and outside the usePortal
    // branch, because ModalInMobile passes `usePortal={usePortal && isOpen}` — the two flip in the
    // SAME update. A release nested under `if (this.props.usePortal)` is therefore unreachable on
    // close: rotating a phone past the 767px breakpoint with the order panel open left the count
    // stuck above zero, `#root` inert forever, and the entire app unclickable with no modal on
    // screen. The old unconditional removeAttribute self-healed that; a reference count cannot, so
    // the invariant "this instance holds a reference exactly while it is an open portal modal" has
    // to be restated on every update rather than inferred from a transition.
    this.syncInertHold();
  }

  syncInertHold() {
    const shouldHold = !!this.props.usePortal && !!this.props.isOpen;
    if (shouldHold && !this.holdsInert) {
      this.holdsInert = true;
      acquireInert();
    } else if (!shouldHold) {
      this.releaseInertMaybe();
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling, focusElementId } = this.props;
    window.document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    window.document.body.removeEventListener('resize', this.handleResize);
    onManageDisableScrolling(id, false);
    this.releaseInertMaybe();
    if (focusElementId) {
      document.getElementById(focusElementId)?.focus({ preventScroll: true });
    }
  }

  releaseInertMaybe() {
    if (this.holdsInert) {
      this.holdsInert = false;
      releaseInert();
    }
  }

  handleBodyKeyUp(event) {
    const { isOpen } = this.props;
    if (event.key === KEY_ESCAPE && isOpen) {
      this.handleClose(event);
    }
  }

  handleClose(event) {
    const { id, onClose, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, false);
    this.releaseInertMaybe();
    onClose(event);
  }

  handleResize() {
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
  }

  render() {
    const {
      children,
      className,
      scrollLayerClassName,
      closeButtonMessage,
      containerClassName,
      contentClassName,
      lightCloseButton,
      intl,
      isClosedClassName = css.isClosed,
      isOpen,
      usePortal,
    } = this.props;

    const closeModalMessage = intl.formatMessage({ id: 'Modal.closeModal' });
    // A new key, not 'Modal.close': that one is stored in capitals ("SCHLIEßEN") and may be
    // overridden in the Console, so lowercasing it here would not reach the live site.
    const closeMessage = closeButtonMessage || intl.formatMessage({ id: 'Modal.closeLabel' });
    const closeButtonClasses = classNames(css.close, {
      [css.closeLight]: lightCloseButton,
    });
    const closeBtn = isOpen ? (
      <nav className={css.marketplaceModalCloseNav} aria-label={closeModalMessage}>
        <Button
          onClick={this.handleClose}
          rootClassName={closeButtonClasses}
          title={closeModalMessage}
          aria-expanded={isOpen}
        >
          <span className={css.closeText}>{closeMessage}</span>
          <IconClose rootClassName={css.closeIcon} ariaLabel={closeMessage} />
        </Button>
      </nav>
    ) : null;

    // Modal uses given styles to wrap child components.
    // If props doesn't contain isClosedClassName, styles default to css.isClosed
    // This makes it possible to create ModalInMobile on top of Modal where style modes are:
    // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
    const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
    const modalClass = isOpen ? isOpenClass : isClosedClassName;
    const classes = classNames(modalClass, className);
    const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
    const containerClasses = containerClassName || css.container;
    const portalRoot = this.state.portalRoot;

    // If you want to use Portal https://reactjs.org/docs/portals.html
    // you need to use 'userPortal' flag.
    // ModalInMobile component needs to use the old Modal without the portal
    // because it's relying that the content is rendered inside
    // the DOM hierarchy of the parent component unlike Modal inside Portal.

    return !usePortal ? (
      <div className={classes}>
        <div className={scrollLayerClasses}>
          <div className={containerClasses}>
            {closeBtn}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    ) : portalRoot ? (
      <Portal portalRoot={portalRoot}>
        <div className={classes}>
          <div className={scrollLayerClasses}>
            <div
              className={classNames(containerClasses, css.focusedDiv)}
              ref={this.refDiv}
              tabIndex="-1"
              role="main"
              aria-label="modal"
            >
              {closeBtn}
              <div className={classNames(contentClassName || css.content)}>{children}</div>
            </div>
          </div>
        </div>
      </Portal>
    ) : null;
  }
}

export default injectIntl(ModalComponent);
